import type * as React from "react";

import { Chip, type ChipTone } from "./chip";
import { cn } from "./cn";

type StatusPillStatus =
  | "auto_synced"
  | "setup_pending"
  | "manual"
  | "reconnect"
  | "vanished"
  | "auto_reconciled"
  | "reconciled"
  | "open"
  | "provisional"
  | "needs_answer"
  | "uncategorized";

type Mark = "check" | "dot" | "sparkle" | "alert" | null;

const statusMap: Record<
  StatusPillStatus,
  { label: string; tone: ChipTone; mark: Mark }
> = {
  auto_synced: { label: "Auto-synced", tone: "success", mark: "check" },
  setup_pending: { label: "Setup pending", tone: "surface", mark: "dot" },
  /** A statement-upload account: nothing syncs, and nothing is pending. */
  manual: { label: "Manual", tone: "surface", mark: null },
  reconnect: { label: "Reconnect", tone: "warning", mark: "alert" },
  vanished: { label: "Disconnected", tone: "danger", mark: null },
  auto_reconciled: {
    label: "Auto-reconciled",
    tone: "success",
    mark: "sparkle",
  },
  reconciled: { label: "Reconciled", tone: "success", mark: "check" },
  open: { label: "Open", tone: "warning", mark: "dot" },
  provisional: { label: "Provisional", tone: "info", mark: null },
  needs_answer: { label: "Needs your answer", tone: "warning", mark: null },
  uncategorized: { label: "Uncategorized", tone: "surface", mark: null },
};

function statusPillLabel(status: StatusPillStatus): string {
  return statusMap[status].label;
}

function Sparkle() {
  return (
    <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
      <path d="M6 0.5 7.3 4.7 11.5 6 7.3 7.3 6 11.5 4.7 7.3 0.5 6 4.7 4.7z" />
    </svg>
  );
}

function Alert() {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 1.5 11 10.5H1z" />
      <path d="M6 5v2.5M6 9v.01" />
    </svg>
  );
}

type StatusPillProps = {
  status: StatusPillStatus;
  /** Replaces the default label. */
  children?: React.ReactNode;
  size?: "sm" | "default";
  className?: string;
};

function StatusPill({ status, children, size, className }: StatusPillProps) {
  const { label, tone, mark } = statusMap[status];
  return (
    <Chip
      tone={tone}
      size={size}
      dot={mark === "dot"}
      check={mark === "check"}
      data-status={status}
      className={cn(
        status === "uncategorized" &&
          "border border-dashed border-muted-foreground bg-transparent text-muted-foreground ring-0",
        className,
      )}
    >
      {mark === "sparkle" ? <Sparkle /> : null}
      {mark === "alert" ? <Alert /> : null}
      {children ?? label}
    </Chip>
  );
}

export { StatusPill, statusPillLabel };
export type { StatusPillProps, StatusPillStatus };
